// modules/WorldNPC/WorldNPCAnimation.js
// NPC动画和移动模块

import DebugHelper from '../DebugHelper.js';
import { GAME_CONSTANTS } from '../GameConstants.js';

export default class WorldNPCAnimation {
    constructor(scene, npcManager) {
        this.scene = scene;
        this.npcManager = npcManager;
    }
    
    /**
     * 设置NPC走动动画（类似玩家动画）
     * @param {Phaser.GameObjects.Sprite} npcSprite - NPC精灵
     * @param {string} texture - 纹理名称
     */
    setupNPCWalkAnimations(npcSprite, texture) {
        if (!npcSprite || !texture) return;
        
        try {
            // 检查纹理是否存在
            if (!this.scene.textures.exists(texture)) {
                DebugHelper.warn(`WorldNPCAnimation: 纹理 ${texture} 不存在，跳过动画设置`);
                return;
            }
            
            // 为每个NPC创建唯一的动画键（避免冲突）
            const animPrefix = `npc_${texture}_`;
            
            // 检查动画是否已创建（相同纹理的NPC共享动画）
            if (!this.scene.anims.exists(animPrefix + 'down')) {
                // 向下动画（移动用，帧率8）
                this.scene.anims.create({
                    key: animPrefix + 'down',
                    frames: this.scene.anims.generateFrameNumbers(texture, { start: 0, end: 3 }),
                    frameRate: 8,
                    repeat: -1
                });
                
                // 向下原地动画（不能移动的NPC用，帧率1，更慢）
                this.scene.anims.create({
                    key: animPrefix + 'down_idle',
                    frames: this.scene.anims.generateFrameNumbers(texture, { start: 0, end: 3 }),
                    frameRate: 1,
                    repeat: -1
                });
                
                // 向上动画
                this.scene.anims.create({
                    key: animPrefix + 'up',
                    frames: this.scene.anims.generateFrameNumbers(texture, { start: 12, end: 15 }),
                    frameRate: 8,
                    repeat: -1
                });
                
                // 向左动画
                this.scene.anims.create({
                    key: animPrefix + 'left',
                    frames: this.scene.anims.generateFrameNumbers(texture, { start: 4, end: 7 }),
                    frameRate: 8,
                    repeat: -1
                });
                
                // 向右动画
                this.scene.anims.create({
                    key: animPrefix + 'right',
                    frames: this.scene.anims.generateFrameNumbers(texture, { start: 8, end: 11 }),
                    frameRate: 8,
                    repeat: -1
                });
            }
            
            // 保存动画前缀到NPC数据中
            npcSprite.setData('animPrefix', animPrefix);
            
            // 默认设置向下站立帧
            npcSprite.setFrame(0);
            
        } catch (error) {
            DebugHelper.warn('WorldNPCAnimation: NPC动画设置失败', error);
        }
    }
    
    /**
     * 设置NPC走动AI（在1格范围内自由走动，只允许上下左右四个方向）
     * @param {Phaser.GameObjects.Sprite} npcSprite - NPC精灵
     * @param {number} index - NPC索引
     */
    setupNPCWalkAI(npcSprite, index) {
        if (!npcSprite) return;
        
        try {
            const tileSize = GAME_CONSTANTS.TILE_SIZE;
            const walkRange = tileSize * 1; // 1格范围
            const originalPos = npcSprite.getData('originalPosition');
            
            if (!originalPos) {
                DebugHelper.warn('WorldNPCAnimation: NPC缺少原始位置信息');
                return;
            }
            
            // 初始化NPC走动状态
            const walkState = {
                currentDirection: 'down', // 当前朝向
                isMoving: false,
                waitTimer: 0,
                waitDuration: 5000 + Math.random() * 5000, // 等待5-10秒
                moveSpeed: 20, // 移动速度（像素/秒），一格32像素，约1.6秒移动一格
                walkRange: walkRange,
                originalTileX: Math.floor(originalPos.x / tileSize), // 原始格子坐标
                originalTileY: Math.floor(originalPos.y / tileSize),
                currentTileX: Math.floor(originalPos.x / tileSize), // 当前格子坐标
                currentTileY: Math.floor(originalPos.y / tileSize),
                moveTween: null
            };
            
            npcSprite.setData('walkState', walkState);
            
            // 设置初始等待时间，让NPC不同时开始移动
            walkState.waitTimer = index * 500 + Math.random() * 1000;
            
        } catch (error) {
            DebugHelper.warn('WorldNPCAnimation: NPC走动AI设置失败', error);
        }
    }
    
    /**
     * 更新NPC状态（每帧调用）
     */
    update() {
        const npcs = this.npcManager.npcs;
        if (!npcs || npcs.length === 0) return;
        
        const deltaTime = this.scene.game.loop.delta; // 获取帧时间（毫秒）
        
        npcs.forEach(npc => {
            if (!npc.sprite || !npc.sprite.active) return;
            
            this.updateNPCWalk(npc.sprite, deltaTime);
        });
    }
    
    /**
     * 更新单个NPC的走动行为
     * @param {Phaser.GameObjects.Sprite} npcSprite - NPC精灵
     * @param {number} deltaTime - 帧时间（毫秒）
     */
    updateNPCWalk(npcSprite, deltaTime) {
        const walkState = npcSprite.getData('walkState');
        if (!walkState) return;
        
        // 如果正在移动，检查是否到达目标
        if (walkState.isMoving) {
            // 检查tween是否完成
            if (!walkState.moveTween || !walkState.moveTween.isActive()) {
                // 如果moveTween已经完成，进入等待状态
                if (!walkState.moveTween) {
                    // 确保进入等待状态
                    walkState.isMoving = false;
                    walkState.waitTimer = 0;
                    walkState.waitDuration = 5000 + Math.random() * 5000; // 等待5-10秒
                    
                    // 停止动画，显示静态帧
                    this.stopNPCAnimation(npcSprite, walkState.currentDirection);
                }
            }
        } else {
            // 等待状态
            walkState.waitTimer += deltaTime;
            
            // 等待时间到了，选择新方向移动
            if (walkState.waitTimer >= walkState.waitDuration) {
                this.chooseNewWalkDirection(npcSprite, walkState);
            }
        }
    }
    
    /**
     * 为NPC选择新的走动方向（只允许上下左右）
     * @param {Phaser.GameObjects.Sprite} npcSprite - NPC精灵
     * @param {Object} walkState - 走动状态对象
     */
    chooseNewWalkDirection(npcSprite, walkState) {
        const tileSize = GAME_CONSTANTS.TILE_SIZE;
        const directions = ['up', 'down', 'left', 'right'];
        
        // 过滤出在范围内且没有障碍物的方向
        const validDirections = directions.filter(dir => {
            let newTileX = walkState.currentTileX;
            let newTileY = walkState.currentTileY;
            
            switch (dir) {
                case 'up':
                    newTileY -= 1;
                    break;
                case 'down':
                    newTileY += 1;
                    break;
                case 'left':
                    newTileX -= 1;
                    break;
                case 'right':
                    newTileX += 1;
                    break;
            }
            
            // 检查是否在1格范围内
            const distanceX = Math.abs(newTileX - walkState.originalTileX);
            const distanceY = Math.abs(newTileY - walkState.originalTileY);
            
            if (distanceX > 1 || distanceY > 1) {
                return false; // 超出范围
            }
            
            // 检查目标位置是否有障碍物
            if (this.npcManager.isTileBlocked(newTileX, newTileY, tileSize, npcSprite)) {
                return false; // 有障碍物
            }
            
            return true;
        });
        
        // 如果没有有效方向，尝试朝向原始位置的方向
        if (validDirections.length === 0) {
            // 计算朝向原始位置的方向
            const dx = walkState.originalTileX - walkState.currentTileX;
            const dy = walkState.originalTileY - walkState.currentTileY;
            
            let backDirection = null;
            if (Math.abs(dx) > Math.abs(dy)) {
                backDirection = dx > 0 ? 'right' : 'left';
            } else if (dy !== 0) {
                backDirection = dy > 0 ? 'down' : 'up';
            }
            
            // 如果找到了往回走的方向，使用它
            if (backDirection && directions.includes(backDirection)) {
                validDirections.push(backDirection);
            } else {
                // 如果还是找不到，不移动
                walkState.waitTimer = 0;
                walkState.waitDuration = 5000 + Math.random() * 5000; // 等待5-10秒
                return;
            }
        }
        
        // 随机选择一个方向
        const direction = validDirections[Math.floor(Math.random() * validDirections.length)];
        
        // 计算目标格子坐标
        let targetTileX = walkState.currentTileX;
        let targetTileY = walkState.currentTileY;
        
        switch (direction) {
            case 'up':
                targetTileY -= 1;
                break;
            case 'down':
                targetTileY += 1;
                break;
            case 'left':
                targetTileX -= 1;
                break;
            case 'right':
                targetTileX += 1;
                break;
        }
        
        // 计算目标像素坐标
        const targetX = targetTileX * tileSize + tileSize / 2;
        const targetY = targetTileY * tileSize + tileSize / 2;
        
        // 停止之前的tween（如果有）
        if (walkState.moveTween) {
            walkState.moveTween.stop();
        }
        
        // 更新方向
        walkState.currentDirection = direction;
        
        // 播放移动动画
        this.playNPCAnimation(npcSprite, direction);
        
        // 计算移动时间（一格32像素）
        const moveDuration = (tileSize / walkState.moveSpeed) * 1000; // 转换为毫秒
        
        // 创建移动tween
        walkState.isMoving = true;
        walkState.tweenCancelled = false; // 添加取消标志
        const moveTween = this.scene.tweens.add({
            targets: npcSprite,
            x: targetX,
            y: targetY,
            duration: moveDuration,
            ease: 'Linear',
            onComplete: () => {
                // 检查tween是否已被取消或替换
                if (walkState.tweenCancelled || walkState.moveTween !== moveTween) {
                    return; // tween已被取消或替换，不执行回调
                }
                
                // 更新当前格子坐标
                walkState.currentTileX = targetTileX;
                walkState.currentTileY = targetTileY;
                walkState.isMoving = false;
                walkState.moveTween = null;
                
                // 重置等待计时器，开始等待
                walkState.waitTimer = 0;
                walkState.waitDuration = 5000 + Math.random() * 5000; // 等待5-10秒
                
                // 停止动画，显示静态帧
                this.stopNPCAnimation(npcSprite, walkState.currentDirection);
            }
        });
        walkState.moveTween = moveTween;
    }
    
    /**
     * 播放NPC移动动画
     * @param {Phaser.GameObjects.Sprite} npcSprite - NPC精灵
     * @param {string} direction - 移动方向
     */
    playNPCAnimation(npcSprite, direction) {
        const animPrefix = npcSprite.getData('animPrefix');
        if (!animPrefix) return; // 如果没有动画前缀，说明纹理不支持动画
        
        const animKey = animPrefix + direction;
        if (this.scene.anims.exists(animKey)) {
            if (!npcSprite.anims.isPlaying || npcSprite.anims.currentAnim?.key !== animKey) {
                npcSprite.play(animKey, true);
            }
        }
    }
    
    /**
     * 播放NPC原地慢速动画（用于不能移动的NPC）
     * @param {Phaser.GameObjects.Sprite} npcSprite - NPC精灵
     */
    playNPCIdleAnimation(npcSprite) {
        const animPrefix = npcSprite.getData('animPrefix');
        if (!animPrefix) return; // 如果没有动画前缀，说明纹理不支持动画
        
        const animKey = animPrefix + 'down_idle';
        if (this.scene.anims.exists(animKey)) {
            if (!npcSprite.anims.isPlaying || npcSprite.anims.currentAnim?.key !== animKey) {
                npcSprite.play(animKey, true);
            }
        } else {
            // 如果慢速动画不存在，回退到普通动画
            this.playNPCAnimation(npcSprite, 'down');
        }
    }
    
    /**
     * 停止NPC动画，显示静态帧
     * @param {Phaser.GameObjects.Sprite} npcSprite - NPC精灵
     * @param {string} direction - 当前方向
     */
    stopNPCAnimation(npcSprite, direction) {
        const animPrefix = npcSprite.getData('animPrefix');
        if (!animPrefix) return; // 如果没有动画前缀，说明纹理不支持动画
        
        npcSprite.anims.stop();
        
        // 根据方向设置静态帧
        const frameMap = {
            'up': 12,
            'down': 0,
            'left': 4,
            'right': 8
        };
        const frame = frameMap[direction] || 0;
        npcSprite.setFrame(frame);
    }
    
    /**
     * 让NPC转向面向玩家
     * @param {Phaser.GameObjects.Sprite} npcSprite - NPC精灵
     * @param {number} playerX - 玩家X坐标
     * @param {number} playerY - 玩家Y坐标
     */
    turnNPCTowardsPlayer(npcSprite, playerX, playerY) {
        if (!npcSprite || !npcSprite.active) {
            DebugHelper.warn('WorldNPCAnimation: NPC精灵无效');
            return;
        }
        
        const npcX = npcSprite.x;
        const npcY = npcSprite.y;
        
        DebugHelper.log(`WorldNPCAnimation: NPC位置 (${npcX.toFixed(1)}, ${npcY.toFixed(1)}), 玩家位置 (${playerX.toFixed(1)}, ${playerY.toFixed(1)})`);
        
        // 计算NPC应该面向的方向（朝向玩家）
        const dx = playerX - npcX;
        const dy = playerY - npcY;
        
        // 如果玩家和NPC在同一位置，不改变方向
        if (Math.abs(dx) < 1 && Math.abs(dy) < 1) {
            DebugHelper.log('WorldNPCAnimation: 玩家和NPC在同一位置，不改变方向');
            return;
        }
        
        let targetDirection = 'down'; // 默认方向
        if (Math.abs(dx) > Math.abs(dy)) {
            targetDirection = dx > 0 ? 'right' : 'left';
        } else if (dy !== 0) {
            targetDirection = dy > 0 ? 'down' : 'up';
        }
        
        // 获取NPC的walkState（如果有）
        const walkState = npcSprite.getData('walkState');
        
        // 注意：不能移动的NPC可能没有walkState，这是正常的
        if (!walkState) {
            DebugHelper.log('WorldNPCAnimation: NPC没有walkState（可能是不能移动的NPC）');
        }
        
        // 如果NPC正在移动，停止移动
        if (walkState && walkState.isMoving && walkState.moveTween) {
            DebugHelper.log('WorldNPCAnimation: 检测到NPC正在移动，准备停止');
            
            // 保存当前tween引用和当前位置
            const currentTween = walkState.moveTween;
            const currentX = npcSprite.x;
            const currentY = npcSprite.y;
            
            // 设置取消标志，防止onComplete回调执行
            walkState.tweenCancelled = true;
            
            // 立即停止动画
            if (npcSprite.anims && npcSprite.anims.isPlaying) {
                npcSprite.anims.stop();
                DebugHelper.log('WorldNPCAnimation: NPC动画已停止');
            }
            
            // 从tween管理器中移除tween
            if (currentTween && this.scene.tweens && typeof this.scene.tweens.remove === 'function') {
                try {
                    this.scene.tweens.remove(currentTween);
                } catch (e) {
                    DebugHelper.warn('移除tween时出错:', e);
                }
            }
            
            // 清除引用和状态（必须在移除tween之后）
            walkState.moveTween = null;
            walkState.isMoving = false;
            
            // 重置等待计时器，确保NPC进入等待状态
            walkState.waitTimer = 0;
            walkState.waitDuration = 5000 + Math.random() * 5000;
            
            // 更新当前格子坐标（基于当前位置）
            const tileSize = GAME_CONSTANTS.TILE_SIZE;
            walkState.currentTileX = Math.floor(currentX / tileSize);
            walkState.currentTileY = Math.floor(currentY / tileSize);
            
            // 确保NPC位置固定（防止tween继续更新）
            npcSprite.x = currentX;
            npcSprite.y = currentY;
            
            DebugHelper.log(`WorldNPCAnimation: NPC移动已停止，固定位置 (${currentX.toFixed(1)}, ${currentY.toFixed(1)}), 格子坐标: (${walkState.currentTileX}, ${walkState.currentTileY})`);
        }
        
        // 更新方向
        if (walkState) {
            walkState.currentDirection = targetDirection;
        }
        
        // 停止当前动画，显示面向玩家的静态帧
        this.stopNPCAnimation(npcSprite, targetDirection);
        
        DebugHelper.log(`NPC转向面向玩家: ${targetDirection}`);
    }
    
    /**
     * 重置所有NPC位置
     */
    resetAllPositions() {
        const tileSize = GAME_CONSTANTS.TILE_SIZE;
        const npcs = this.npcManager.npcs;
        
        npcs.forEach(npc => {
            if (npc.sprite && npc.data.originalPosition) {
                const { x, y } = npc.data.originalPosition;
                
                // 停止当前移动
                const walkState = npc.sprite.getData('walkState');
                if (walkState && walkState.moveTween) {
                    walkState.moveTween.stop();
                    walkState.moveTween = null;
                    walkState.isMoving = false;
                }
                
                // 重置位置
                npc.sprite.x = x;
                npc.sprite.y = y;
                
                // 重置走动状态
                if (walkState) {
                    walkState.currentTileX = Math.floor(x / tileSize);
                    walkState.currentTileY = Math.floor(y / tileSize);
                    walkState.waitTimer = 0;
                    walkState.currentDirection = 'down';
                    
                    // 停止动画，显示静态帧
                    this.stopNPCAnimation(npc.sprite, 'down');
                }
            }
        });
    }
}

